import { useQuery } from '@tanstack/react-query'
import { useMatch } from '@tanstack/react-router'

import type { BranchRef } from '@/bindings/BranchRef'
import type { RestaurantContext } from '@/bindings/RestaurantContext'
import { restaurantContextQuery } from '@/features/back-office/api'

/** The restaurant manage layout every restaurant-level page nests under (URL scheme, 2026-09-11). */
export const RESTAURANT_SCOPE_ROUTE = '/$restaurantSlug/manage'
/** The branch manage layout every branch-level page nests under. */
export const BRANCH_SCOPE_ROUTE = '/$restaurantSlug/$branchSlug/manage'

export interface RestaurantScope {
  restaurantSlug: string
  /** Undefined until the context resolves the slug (the API is id-keyed). */
  id: string | undefined
  /** Undefined while loading or after a failed load. */
  context: RestaurantContext | undefined
  /** Set on a branch-scoped page; `branch` resolves once the context lists it (never for one out of scope). */
  branchSlug: string | undefined
  branch: BranchRef | undefined
}

/** Which restaurant (and branch) the current page belongs to, if any; the shell reads it above the route. */
export function useRestaurantScope(): RestaurantScope | undefined {
  const match = useMatch({ from: RESTAURANT_SCOPE_ROUTE, shouldThrow: false })
  const branchMatch = useMatch({ from: BRANCH_SCOPE_ROUTE, shouldThrow: false })
  const restaurantSlug = match?.params.restaurantSlug ?? branchMatch?.params.restaurantSlug
  const context = useQuery({ ...restaurantContextQuery(restaurantSlug ?? ''), enabled: restaurantSlug !== undefined })
  if (restaurantSlug === undefined) return undefined
  const branchSlug = branchMatch?.params.branchSlug
  return {
    restaurantSlug,
    id: context.data?.settings.id,
    context: context.data,
    branchSlug,
    branch: branchSlug === undefined ? undefined : context.data?.branches.find((branch) => branch.slug === branchSlug),
  }
}

export interface ManagedRestaurant {
  restaurantSlug: string
  restaurantId: string
  context: RestaurantContext
}

export interface ManagedBranch extends ManagedRestaurant {
  branchSlug: string
  branchId: string
  branch: BranchRef
}

/** A restaurant-level page's scope; the manage layout above guarantees the context is loaded. */
export function useManagedRestaurant(): ManagedRestaurant {
  const scope = useRestaurantScope()
  if (!scope?.context) throw new Error('useManagedRestaurant: rendered outside the restaurant manage layout')
  return { restaurantSlug: scope.restaurantSlug, restaurantId: scope.context.settings.id, context: scope.context }
}

/** A branch-level page's scope; the branch manage layout above guarantees the branch is in scope. */
export function useManagedBranch(): ManagedBranch {
  const scope = useRestaurantScope()
  if (!scope?.context || scope.branchSlug === undefined || !scope.branch) {
    throw new Error('useManagedBranch: rendered outside the branch manage layout')
  }
  return {
    restaurantSlug: scope.restaurantSlug,
    restaurantId: scope.context.settings.id,
    context: scope.context,
    branchSlug: scope.branchSlug,
    branchId: scope.branch.id,
    branch: scope.branch,
  }
}
